/**
 * In-memory portal rollout campaigns for the mock API client (tracker item
 * 81). Same rules as the backend where the UI depends on them: ADMIN and
 * OFFICE_MANAGER only, the skip reasons in the same order (NO_EMAIL,
 * INVALID_ADDRESS, EMAIL_INVALID, OPTED_OUT, ALREADY_HAS_LOGIN,
 * ALREADY_INVITED, EMAIL_IN_USE, DUPLICATE_EMAIL; the oldest customer wins a
 * shared address), preview writes nothing, a DRAFT snapshot, the campaign
 * states, blockers (409 with the blocker code), batches cut to the batch size
 * and the engine rate, the bulk and single resend rules, activation read from
 * the login, CSV with formula escaping. Not modelled: the worker and the batch
 * gap (the office's "Send next batch" does the sending), bounces, the 20,000
 * candidate cap beyond the check, audit rows.
 */
import { AxiosError, AxiosHeaders, AxiosResponse } from 'axios';
import * as types from './types';

function apiError(status: number, message: string, code?: string): AxiosError {
  const config = { headers: new AxiosHeaders() };
  const response = { status, statusText: '', headers: {}, config, data: { success: false, message, ...(code ? { code } : {}) } } as AxiosResponse;
  return new AxiosError(message, undefined, config, undefined, response);
}

export interface MockRolloutUser {
  id: string;
  tenantId: string;
  role: string;
  email: string;
}

export interface MockRolloutCustomer {
  id: string;
  name: string;
  email?: string | null;
  status: string;
  createdAt: string;
  externalRef?: string | null;
  notificationOptOut?: boolean;
  emailInvalid?: boolean;
}

export type MockLoginState = 'PENDING_EMAIL' | 'ACTIVE' | 'DISABLED' | 'MISSING';

export interface MockRolloutHost {
  /** The signed-in user (throws when signed out) */
  currentUser(): MockRolloutUser;
  customers(tenantId: string): MockRolloutCustomer[];
  /** Any portal login linked to the customer */
  hasLogin(tenantId: string, customerId: string): boolean;
  /** A user of the company (staff or portal) already has this address */
  emailInUse(tenantId: string, email: string): boolean;
  readiness(tenantId: string): { portalEnabled: boolean; notificationsEnabled: boolean; eventActive: boolean; connectionVerified: boolean };
  /** Create the pending login (or give the pending one a new link); returns its user id */
  invite(tenantId: string, customerId: string, email: string, existingUserId: string | null): string;
  loginState(userId: string): MockLoginState;
}

const STAFF = ['ADMIN', 'OFFICE_MANAGER'];
const DAY = 24 * 60 * 60 * 1000;
const MINUTE = 60 * 1000;
const INVITE_DAYS = 7;
const MAX_CANDIDATES = 20_000;
const ENGINE_RATE = 60;
const CUSTOMER_STATUSES = ['LEAD', 'PROSPECT', 'ACTIVE', 'INACTIVE', 'LOST'];
const RECIPIENT_STATUSES = ['PENDING', 'SENT', 'SKIPPED', 'FAILED', 'ACTIVATED', 'EXPIRED', 'BOUNCED'];
const CAMPAIGN_STATUSES = ['DRAFT', 'RUNNING', 'PAUSED', 'COMPLETED', 'CANCELLED'];

export const MOCK_ROLLOUT_SKIP_TEXT: Record<string, string> = {
  NO_EMAIL: 'The customer has no email address',
  INVALID_ADDRESS: 'The email address is not valid',
  EMAIL_INVALID: 'The email address bounced or reported spam',
  OPTED_OUT: 'The customer opted out of notifications',
  ALREADY_HAS_LOGIN: 'The customer already has a portal login',
  ALREADY_INVITED: 'The customer is still waiting in another rollout campaign',
  EMAIL_IN_USE: 'Another user of the company already has this email address',
  DUPLICATE_EMAIL: 'Another customer with the same email address is invited instead',
  CAMPAIGN_CANCELLED: 'The campaign was cancelled before the invitation was sent',
};

export const MOCK_ROLLOUT_BLOCKER_TEXT: Record<types.RolloutBlockerCode, string> = {
  PORTAL_DISABLED: 'The customer portal is turned off (Settings > Company)',
  NOTIFICATIONS_DISABLED: 'Notifications are turned off for this company (Settings > Notifications)',
  EVENT_DISABLED: 'The "welcome to the new system" message is turned off (Settings > Notifications > Templates)',
  NOT_CONFIGURED: 'No verified email sending connection (Settings > Notifications)',
};

interface Recipient {
  id: string;
  tenantId: string;
  campaignId: string;
  customerId: string;
  customerName: string;
  position: number;
  status: types.RolloutRecipientStatus;
  skipReason: string | null;
  email: string | null;
  portalUserId: string | null;
  sendCount: number;
  round: number;
  resendQueued: boolean;
  firstSentAt: string | null;
  lastSentAt: string | null;
  inviteExpiresAt: string | null;
  activatedAt: string | null;
  lastError: string | null;
}

interface Campaign {
  id: string;
  tenantId: string;
  name: string;
  status: types.RolloutCampaignStatus;
  filters: types.RolloutFilters;
  batchSize: number;
  batchGapMinutes: number;
  minResendDays: number;
  maxResends: number;
  currentRound: number;
  blockedReason: string | null;
  createdAt: string;
  startedAt: string | null;
  pausedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  lastBatchAt: string | null;
}

interface Classified {
  customer: MockRolloutCustomer;
  email: string | null;
  skipReason: string | null;
}

let sequence = 0;
const nextId = (prefix: string) => `${prefix}-${++sequence}`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function invalid(message: string): AxiosError {
  return apiError(400, message, 'VALIDATION_ERROR');
}

export function csvCell(value: string | number | null | undefined): string {
  let text = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export class MockPortalRollout {
  private campaigns: Campaign[] = [];
  private recipients: Recipient[] = [];

  constructor(private readonly host: MockRolloutHost, private readonly clock: () => Date = () => new Date()) {}

  // ---------------------------------------------------------------- helpers

  private staff(): MockRolloutUser {
    const actor = this.host.currentUser();
    if (!STAFF.includes(actor.role)) throw apiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    return actor;
  }

  private now(): Date {
    return this.clock();
  }

  private blockers(tenantId: string): types.RolloutBlocker[] {
    const r = this.host.readiness(tenantId);
    const codes: types.RolloutBlockerCode[] = [];
    if (!r.portalEnabled) codes.push('PORTAL_DISABLED');
    if (!r.notificationsEnabled) codes.push('NOTIFICATIONS_DISABLED');
    if (!r.eventActive) codes.push('EVENT_DISABLED');
    if (!r.connectionVerified) codes.push('NOT_CONFIGURED');
    return codes.map((code) => ({ code, message: MOCK_ROLLOUT_BLOCKER_TEXT[code] }));
  }

  private find(tenantId: string, id: string): Campaign {
    const campaign = this.campaigns.find((c) => c.id === id && c.tenantId === tenantId);
    if (!campaign) throw apiError(404, 'Campaign not found', 'NOT_FOUND');
    return campaign;
  }

  private recipientsOf(campaign: Campaign): Recipient[] {
    return this.recipients.filter((r) => r.campaignId === campaign.id).sort((a, b) => a.position - b.position);
  }

  /** Expire old invitations and read activation off the login (what the server does when a campaign is opened). */
  private housekeeping(tenantId: string): void {
    const now = this.now().getTime();
    for (const r of this.recipients) {
      if (r.tenantId !== tenantId || !r.portalUserId) continue;
      if ((r.status === 'SENT' || r.status === 'EXPIRED') && this.host.loginState(r.portalUserId) === 'ACTIVE') {
        r.status = 'ACTIVATED';
        r.activatedAt = this.now().toISOString();
        r.resendQueued = false;
      } else if (r.status === 'SENT' && r.inviteExpiresAt && new Date(r.inviteExpiresAt).getTime() <= now) {
        r.status = 'EXPIRED';
      }
    }
    for (const c of this.campaigns) {
      if (c.tenantId === tenantId && (c.status === 'RUNNING' || c.status === 'PAUSED') && !this.hasWork(c)) this.complete(c);
    }
  }

  private hasWork(campaign: Campaign): boolean {
    return this.recipientsOf(campaign).some((r) => r.status === 'PENDING' || r.resendQueued);
  }

  private complete(campaign: Campaign): void {
    if (campaign.status === 'RUNNING') {
      campaign.status = 'COMPLETED';
      campaign.completedAt = this.now().toISOString();
      campaign.blockedReason = null;
    }
  }

  private counts(campaign: Campaign): types.RolloutCounts {
    const rows = this.recipientsOf(campaign);
    const by = (status: string) => rows.filter((r) => r.status === status).length;
    return {
      total: rows.length,
      pending: by('PENDING'),
      sent: by('SENT'),
      skipped: by('SKIPPED'),
      failed: by('FAILED'),
      activated: by('ACTIVATED'),
      expired: by('EXPIRED'),
      bounced: by('BOUNCED'),
      resendQueued: rows.filter((r) => r.resendQueued).length,
      invited: rows.filter((r) => r.sendCount >= 1).length,
    };
  }

  private campaignView(campaign: Campaign): types.RolloutCampaign {
    const counts = this.counts(campaign);
    const gap = campaign.batchGapMinutes * MINUTE;
    const nextBatch = campaign.status === 'RUNNING' && !campaign.blockedReason
      ? new Date(Math.max(this.now().getTime(), (campaign.lastBatchAt ? new Date(campaign.lastBatchAt).getTime() : 0) + gap)).toISOString()
      : null;
    return {
      id: campaign.id,
      name: campaign.name,
      status: campaign.status,
      filters: { ...campaign.filters },
      batchSize: campaign.batchSize,
      batchGapMinutes: campaign.batchGapMinutes,
      minResendDays: campaign.minResendDays,
      maxResends: campaign.maxResends,
      currentRound: campaign.currentRound,
      blockedReason: campaign.blockedReason,
      blockedReasonText: campaign.blockedReason ? MOCK_ROLLOUT_BLOCKER_TEXT[campaign.blockedReason as types.RolloutBlockerCode] ?? campaign.blockedReason : null,
      counts,
      activationRate: counts.invited === 0 ? null : Math.round((counts.activated / counts.invited) * 10_000) / 10_000,
      createdAt: campaign.createdAt,
      startedAt: campaign.startedAt,
      pausedAt: campaign.pausedAt,
      completedAt: campaign.completedAt,
      cancelledAt: campaign.cancelledAt,
      lastBatchAt: campaign.lastBatchAt,
      nextBatchAt: nextBatch,
    };
  }

  private resendProblem(campaign: Campaign, r: Recipient, bulk: boolean): string | null {
    if (campaign.status === 'DRAFT' || campaign.status === 'CANCELLED') return 'CAMPAIGN_NOT_ACTIVE';
    if (!['SENT', 'EXPIRED', 'FAILED'].includes(r.status)) return 'NOT_RESENDABLE';
    if (r.status === 'FAILED' && bulk) return 'NOT_RESENDABLE';
    if (r.resendQueued) return 'ALREADY_QUEUED';
    if (r.sendCount >= 1 + campaign.maxResends) return 'RESEND_LIMIT';
    if (bulk && r.lastSentAt && this.now().getTime() - new Date(r.lastSentAt).getTime() < campaign.minResendDays * DAY) return 'TOO_SOON';
    return null;
  }

  private recipientView(campaign: Campaign, r: Recipient): types.RolloutRecipient {
    const blocked = this.resendProblem(campaign, r, false);
    return {
      id: r.id,
      customerId: r.customerId,
      customerName: r.customerName,
      email: r.email,
      status: r.status,
      skipReason: r.skipReason,
      skipReasonText: r.skipReason ? MOCK_ROLLOUT_SKIP_TEXT[r.skipReason] ?? r.skipReason : null,
      sendCount: r.sendCount,
      round: r.round,
      resendQueued: r.resendQueued,
      firstSentAt: r.firstSentAt,
      lastSentAt: r.lastSentAt,
      inviteExpiresAt: r.inviteExpiresAt,
      activatedAt: r.activatedAt,
      bouncedAt: null,
      lastError: r.lastError,
      canResend: blocked === null,
      resendBlockedReason: blocked,
    };
  }

  private page<T>(rows: T[], page: number, limit: number): types.RolloutPage<T> {
    const start = (page - 1) * limit;
    return { data: rows.slice(start, start + limit), pagination: { page, limit, total: rows.length, pages: Math.max(1, Math.ceil(rows.length / limit)) } };
  }

  private paging(query: { page?: unknown; limit?: unknown } | undefined, defaultLimit: number): { page: number; limit: number } {
    const whole = (value: unknown, name: string, min: number, max: number, fallback: number): number => {
      if (value === undefined || value === '') return fallback;
      const n = Number(value);
      if (!Number.isInteger(n) || n < min || n > max) throw invalid(`${name}: must be a whole number from ${min} to ${max}`);
      return n;
    };
    return { page: whole(query?.page, 'page', 1, 100_000, 1), limit: whole(query?.limit, 'limit', 1, 200, defaultLimit) };
  }

  // ---------------------------------------------------------------- input

  private parseInput(input: types.RolloutCampaignInput, requireName: boolean): {
    name: string;
    filters: Required<Pick<types.RolloutFilters, 'customerStatuses' | 'importedOnly'>> & types.RolloutFilters;
    batchSize: number;
    batchGapMinutes: number;
    minResendDays: number;
    maxResends: number;
  } {
    const body = (input ?? {}) as Record<string, unknown>;
    const whole = (name: string, value: unknown, min: number, max: number, fallback: number): number => {
      if (value === undefined) return fallback;
      if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) throw invalid(`${name}: must be a whole number from ${min} to ${max}`);
      return value;
    };
    let name = '';
    if (requireName) {
      name = typeof body.name === 'string' ? body.name.trim() : '';
      if (name.length < 1 || name.length > 120) throw invalid('name: must be 1 to 120 characters');
    }
    const f = (body.filters ?? {}) as Record<string, unknown>;
    let customerStatuses: string[] = ['ACTIVE'];
    if (f.customerStatuses !== undefined) {
      if (!Array.isArray(f.customerStatuses) || f.customerStatuses.length === 0 || f.customerStatuses.some((s) => !CUSTOMER_STATUSES.includes(s as string))) {
        throw invalid(`filters.customerStatuses: pick at least one of ${CUSTOMER_STATUSES.join(', ')}`);
      }
      customerStatuses = Array.from(new Set(f.customerStatuses as string[]));
    }
    if (f.importedOnly !== undefined && typeof f.importedOnly !== 'boolean') throw invalid('filters.importedOnly: must be true or false');
    const filters: types.RolloutFilters & { customerStatuses: types.RolloutCustomerStatus[]; importedOnly: boolean } = {
      customerStatuses: customerStatuses as types.RolloutCustomerStatus[],
      importedOnly: f.importedOnly === true,
    };
    if (f.customerIds !== undefined) {
      if (!Array.isArray(f.customerIds) || f.customerIds.length > 5000 || f.customerIds.some((id) => typeof id !== 'string')) throw invalid('filters.customerIds: up to 5000 ids');
      filters.customerIds = f.customerIds as string[];
    }
    if (f.limit !== undefined) filters.limit = whole('filters.limit', f.limit, 1, 20_000, 0);
    return {
      name,
      filters,
      batchSize: whole('batchSize', body.batchSize, 1, 200, 50),
      batchGapMinutes: whole('batchGapMinutes', body.batchGapMinutes, 0, 1440, 5),
      minResendDays: whole('minResendDays', body.minResendDays, 0, 90, 3),
      maxResends: whole('maxResends', body.maxResends, 0, 10, 3),
    };
  }

  /** Candidates in creation order with the first matching skip reason; `limit` cuts the eligible ones. */
  private classify(tenantId: string, filters: types.RolloutFilters): { entries: Classified[]; beyondLimit: number } {
    const statuses = filters.customerStatuses ?? ['ACTIVE'];
    let customers = this.host.customers(tenantId).filter((c) => statuses.includes(c.status as types.RolloutCustomerStatus));
    if (filters.importedOnly) customers = customers.filter((c) => !!c.externalRef);
    if (filters.customerIds) customers = customers.filter((c) => filters.customerIds!.includes(c.id));
    customers = [...customers].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
    if (customers.length > MAX_CANDIDATES) {
      throw apiError(422, `More than ${MAX_CANDIDATES} customers match; narrow the filters or set a limit`, 'TOO_MANY_CANDIDATES');
    }
    const waiting = new Set(
      this.recipients
        .filter((r) => r.tenantId === tenantId && r.status === 'PENDING' && ['DRAFT', 'RUNNING', 'PAUSED'].includes(this.campaigns.find((c) => c.id === r.campaignId)?.status ?? ''))
        .map((r) => r.customerId)
    );
    const taken = new Set<string>();
    const entries: Classified[] = customers.map((customer) => {
      const email = customer.email?.trim() || null;
      let skipReason: string | null = null;
      if (!email) skipReason = 'NO_EMAIL';
      else if (!EMAIL_RE.test(email)) skipReason = 'INVALID_ADDRESS';
      else if (customer.emailInvalid) skipReason = 'EMAIL_INVALID';
      else if (customer.notificationOptOut) skipReason = 'OPTED_OUT';
      else if (this.host.hasLogin(tenantId, customer.id)) skipReason = 'ALREADY_HAS_LOGIN';
      else if (waiting.has(customer.id)) skipReason = 'ALREADY_INVITED';
      else if (this.host.emailInUse(tenantId, email)) skipReason = 'EMAIL_IN_USE';
      else if (taken.has(email.toLowerCase())) skipReason = 'DUPLICATE_EMAIL';
      if (!skipReason && email) taken.add(email.toLowerCase());
      return { customer, email, skipReason };
    });
    let beyondLimit = 0;
    if (filters.limit) {
      let eligible = 0;
      const kept = entries.filter((e) => {
        if (e.skipReason) return true;
        eligible++;
        if (eligible > filters.limit!) {
          beyondLimit++;
          return false;
        }
        return true;
      });
      return { entries: kept, beyondLimit };
    }
    return { entries, beyondLimit };
  }

  // ---------------------------------------------------------------- reads

  readiness(): types.RolloutReadiness {
    const actor = this.staff();
    const blockers = this.blockers(actor.tenantId);
    return { ready: blockers.length === 0, blockers, welcomeTemplateActive: this.host.readiness(actor.tenantId).eventActive, engineRatePerMinute: ENGINE_RATE };
  }

  preview(input: types.RolloutCampaignInput): types.RolloutPreview {
    const actor = this.staff();
    const parsed = this.parseInput(input, false);
    const { entries, beyondLimit } = this.classify(actor.tenantId, parsed.filters);
    const eligible = entries.filter((e) => !e.skipReason);
    const skipped = entries.filter((e) => e.skipReason);
    const bySkipReason: Record<string, number> = {};
    skipped.forEach((e) => { bySkipReason[e.skipReason!] = (bySkipReason[e.skipReason!] ?? 0) + 1; });
    const batches = Math.ceil(eligible.length / parsed.batchSize);
    return {
      totalCandidates: entries.length + beyondLimit,
      eligible: eligible.length,
      skipped: skipped.length,
      beyondLimit,
      bySkipReason,
      blockers: this.blockers(actor.tenantId),
      batches,
      estimatedMinutes: batches === 0 ? 0 : (batches - 1) * parsed.batchGapMinutes,
      sampleEligible: eligible.slice(0, 20).map((e) => ({ customerId: e.customer.id, customerName: e.customer.name, email: e.email })),
      skippedCustomers: skipped.slice(0, 200).map((e) => ({
        customerId: e.customer.id,
        customerName: e.customer.name,
        email: e.email,
        reason: e.skipReason!,
        reasonText: MOCK_ROLLOUT_SKIP_TEXT[e.skipReason!] ?? e.skipReason!,
      })),
      skippedTruncated: skipped.length > 200,
    };
  }

  list(query: types.RolloutCampaignListQuery = {}): types.RolloutPage<types.RolloutCampaign> {
    const actor = this.staff();
    if (query.status !== undefined && !CAMPAIGN_STATUSES.includes(query.status)) throw invalid(`status: must be one of ${CAMPAIGN_STATUSES.join(', ')}`);
    const { page, limit } = this.paging(query, 20);
    this.housekeeping(actor.tenantId);
    const rows = this.campaigns
      .filter((c) => c.tenantId === actor.tenantId && (!query.status || c.status === query.status))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt) || b.id.localeCompare(a.id));
    const result = this.page(rows, page, limit);
    return { data: result.data.map((c) => this.campaignView(c)), pagination: result.pagination };
  }

  get(id: string, query: types.RolloutRecipientQuery = {}): types.RolloutCampaignDetail {
    const actor = this.staff();
    if (query.recipientStatus !== undefined && !RECIPIENT_STATUSES.includes(query.recipientStatus)) {
      throw invalid(`recipientStatus: must be one of ${RECIPIENT_STATUSES.join(', ')}`);
    }
    const { page, limit } = this.paging(query, 50);
    const campaign = this.find(actor.tenantId, id);
    this.housekeeping(actor.tenantId);
    const search = (query.search ?? '').trim().toLowerCase();
    const rows = this.recipientsOf(campaign).filter(
      (r) =>
        (!query.recipientStatus || r.status === query.recipientStatus) &&
        (!search || r.customerName.toLowerCase().includes(search) || (r.email ?? '').toLowerCase().includes(search))
    );
    const result = this.page(rows, page, limit);
    return { campaign: this.campaignView(campaign), recipients: { data: result.data.map((r) => this.recipientView(campaign, r)), pagination: result.pagination } };
  }

  customerView(customerId: string): types.CustomerRollout {
    const actor = this.staff();
    if (!this.host.customers(actor.tenantId).some((c) => c.id === customerId)) throw apiError(404, 'Customer not found', 'NOT_FOUND');
    this.housekeeping(actor.tenantId);
    const rows = this.recipients
      .filter((r) => r.tenantId === actor.tenantId && r.customerId === customerId)
      .map((r) => ({ r, c: this.campaigns.find((c) => c.id === r.campaignId)! }))
      .sort((a, b) => b.c.createdAt.localeCompare(a.c.createdAt) || b.c.id.localeCompare(a.c.id));
    const sent = rows.filter(({ r }) => r.lastSentAt).map(({ r }) => r.lastSentAt as string).sort();
    return {
      customerId,
      lastInvitedAt: sent.length ? sent[sent.length - 1] : null,
      status: rows[0]?.r.status ?? null,
      invitations: rows.map(({ r, c }) => ({
        campaignId: c.id,
        campaignName: c.name,
        campaignStatus: c.status,
        recipientId: r.id,
        status: r.status,
        sendCount: r.sendCount,
        lastSentAt: r.lastSentAt,
        activatedAt: r.activatedAt,
      })),
    };
  }

  exportCsv(id: string): types.CsvDownload {
    const actor = this.staff();
    const campaign = this.find(actor.tenantId, id);
    this.housekeeping(actor.tenantId);
    const head = ['Customer', 'Email', 'Status', 'Skip reason', 'First sent', 'Last sent', 'Activated', 'Sends', 'Round'];
    const lines = [head.join(',')];
    for (const r of this.recipientsOf(campaign)) {
      lines.push([r.customerName, r.email, r.status, r.skipReason ? MOCK_ROLLOUT_SKIP_TEXT[r.skipReason] ?? r.skipReason : '', r.firstSentAt, r.lastSentAt, r.activatedAt, r.sendCount, r.round].map(csvCell).join(','));
    }
    return { blob: new Blob([`${lines.join('\r\n')}\r\n`], { type: 'text/csv' }), filename: 'portal-rollout-recipients.csv' };
  }

  // ---------------------------------------------------------------- writes

  create(input: types.RolloutCampaignInput): types.RolloutCampaign {
    const actor = this.staff();
    const parsed = this.parseInput(input, true);
    const { entries } = this.classify(actor.tenantId, parsed.filters);
    if (!entries.some((e) => !e.skipReason)) throw apiError(422, 'No customer can be invited with these filters', 'NOTHING_TO_INVITE');
    const now = this.now().toISOString();
    const campaign: Campaign = {
      id: nextId('rollout'),
      tenantId: actor.tenantId,
      name: parsed.name,
      status: 'DRAFT',
      filters: parsed.filters,
      batchSize: parsed.batchSize,
      batchGapMinutes: parsed.batchGapMinutes,
      minResendDays: parsed.minResendDays,
      maxResends: parsed.maxResends,
      currentRound: 1,
      blockedReason: null,
      createdAt: now,
      startedAt: null,
      pausedAt: null,
      completedAt: null,
      cancelledAt: null,
      lastBatchAt: null,
    };
    this.campaigns.push(campaign);
    entries.forEach((e, index) => {
      this.recipients.push({
        id: nextId('rcpt'),
        tenantId: actor.tenantId,
        campaignId: campaign.id,
        customerId: e.customer.id,
        customerName: e.customer.name,
        position: index,
        status: e.skipReason ? 'SKIPPED' : 'PENDING',
        skipReason: e.skipReason,
        email: e.email,
        portalUserId: null,
        sendCount: 0,
        round: 0,
        resendQueued: false,
        firstSentAt: null,
        lastSentAt: null,
        inviteExpiresAt: null,
        activatedAt: null,
        lastError: null,
      });
    });
    return this.campaignView(campaign);
  }

  private assertNoBlockers(tenantId: string): void {
    const blockers = this.blockers(tenantId);
    if (blockers.length) throw apiError(409, blockers[0].message, blockers[0].code);
  }

  start(id: string): types.RolloutCampaign {
    const actor = this.staff();
    const campaign = this.find(actor.tenantId, id);
    if (campaign.status !== 'DRAFT') throw apiError(409, 'Only a draft campaign can be started', 'INVALID_STATE');
    this.assertNoBlockers(actor.tenantId);
    if (!this.hasWork(campaign)) throw apiError(409, 'There is nobody left to send to', 'NOTHING_TO_SEND');
    campaign.status = 'RUNNING';
    campaign.startedAt = this.now().toISOString();
    return this.campaignView(campaign);
  }

  pause(id: string): types.RolloutCampaign {
    const actor = this.staff();
    const campaign = this.find(actor.tenantId, id);
    if (campaign.status !== 'RUNNING') throw apiError(409, 'Only a running campaign can be paused', 'INVALID_STATE');
    campaign.status = 'PAUSED';
    campaign.pausedAt = this.now().toISOString();
    return this.campaignView(campaign);
  }

  resume(id: string): types.RolloutCampaign {
    const actor = this.staff();
    const campaign = this.find(actor.tenantId, id);
    if (campaign.status !== 'PAUSED') throw apiError(409, 'Only a paused campaign can be resumed', 'INVALID_STATE');
    this.assertNoBlockers(actor.tenantId);
    campaign.status = 'RUNNING';
    campaign.pausedAt = null;
    return this.campaignView(campaign);
  }

  cancel(id: string): types.RolloutCampaign {
    const actor = this.staff();
    const campaign = this.find(actor.tenantId, id);
    if (!['DRAFT', 'RUNNING', 'PAUSED'].includes(campaign.status)) throw apiError(409, 'This campaign can no longer be cancelled', 'INVALID_STATE');
    for (const r of this.recipientsOf(campaign)) {
      if (r.status === 'PENDING') {
        r.status = 'SKIPPED';
        r.skipReason = 'CAMPAIGN_CANCELLED';
      }
      r.resendQueued = false;
    }
    campaign.status = 'CANCELLED';
    campaign.cancelledAt = this.now().toISOString();
    campaign.blockedReason = null;
    return this.campaignView(campaign);
  }

  /** One invitation: skip when the customer changed, else (re)issue the login and mark SENT. */
  private deliver(campaign: Campaign, r: Recipient): 'sent' | 'skipped' {
    const customer = this.host.customers(r.tenantId).find((c) => c.id === r.customerId);
    const email = customer?.email?.trim() || r.email;
    const problem = !customer || !email ? 'NO_EMAIL'
      : customer.emailInvalid ? 'EMAIL_INVALID'
      : customer.notificationOptOut ? 'OPTED_OUT'
      : !r.portalUserId && this.host.hasLogin(r.tenantId, r.customerId) ? 'ALREADY_HAS_LOGIN'
      : null;
    if (problem) {
      if (r.sendCount === 0) {
        r.status = 'SKIPPED';
        r.skipReason = problem;
      }
      r.resendQueued = false;
      return 'skipped';
    }
    const now = this.now();
    r.portalUserId = this.host.invite(r.tenantId, r.customerId, email as string, r.portalUserId);
    r.email = email;
    r.status = 'SENT';
    r.skipReason = null;
    r.sendCount += 1;
    r.round = campaign.currentRound;
    r.resendQueued = false;
    r.firstSentAt = r.firstSentAt ?? now.toISOString();
    r.lastSentAt = now.toISOString();
    r.inviteExpiresAt = new Date(now.getTime() + INVITE_DAYS * DAY).toISOString();
    r.lastError = null;
    return 'sent';
  }

  sendBatch(id: string): types.RolloutBatchResult {
    const actor = this.staff();
    const campaign = this.find(actor.tenantId, id);
    if (campaign.status !== 'RUNNING') throw apiError(409, 'Only a running campaign can send', 'INVALID_STATE');
    this.assertNoBlockers(actor.tenantId);
    const room = ENGINE_RATE;
    const size = Math.min(campaign.batchSize, room);
    const due = this.recipientsOf(campaign).filter((r) => r.status === 'PENDING' || r.resendQueued).slice(0, size);
    const batch = { attempted: due.length, sent: 0, skipped: 0, failed: 0, deferred: 0, limitedByRate: size < campaign.batchSize };
    for (const r of due) {
      if (this.deliver(campaign, r) === 'sent') batch.sent += 1;
      else batch.skipped += 1;
    }
    campaign.lastBatchAt = this.now().toISOString();
    if (!this.hasWork(campaign)) this.complete(campaign);
    return { campaign: this.campaignView(campaign), batch };
  }

  resendAll(id: string): types.RolloutResendResult {
    const actor = this.staff();
    const campaign = this.find(actor.tenantId, id);
    if (campaign.status === 'DRAFT' || campaign.status === 'CANCELLED') throw apiError(409, 'Start the campaign before sending a follow-up', 'INVALID_STATE');
    this.housekeeping(actor.tenantId);
    const notQueued: Record<string, number> = {};
    const eligible: Recipient[] = [];
    for (const r of this.recipientsOf(campaign)) {
      if (!['SENT', 'EXPIRED'].includes(r.status)) continue;
      const customer = this.host.customers(actor.tenantId).find((c) => c.id === r.customerId);
      const reason = this.resendProblem(campaign, r, true)
        ?? (customer?.notificationOptOut ? 'OPTED_OUT'
          : customer?.emailInvalid ? 'EMAIL_INVALID'
          : r.portalUserId && this.host.loginState(r.portalUserId) === 'DISABLED' ? 'LOGIN_DISABLED'
          : null);
      if (reason) notQueued[reason] = (notQueued[reason] ?? 0) + 1;
      else eligible.push(r);
    }
    if (eligible.length === 0) throw apiError(409, 'Nobody can be sent a follow-up yet', 'NOTHING_TO_RESEND');
    eligible.forEach((r) => { r.resendQueued = true; });
    campaign.currentRound += 1;
    campaign.status = 'RUNNING';
    campaign.completedAt = null;
    campaign.pausedAt = null;
    return { campaign: this.campaignView(campaign), queued: eligible.length, notQueued };
  }

  resendOne(id: string, recipientId: string): types.RolloutRecipientResendResult {
    const actor = this.staff();
    const campaign = this.find(actor.tenantId, id);
    const r = this.recipients.find((x) => x.id === recipientId && x.campaignId === campaign.id);
    if (!r) throw apiError(404, 'Recipient not found', 'NOT_FOUND');
    this.housekeeping(actor.tenantId);
    if (campaign.status === 'DRAFT' || campaign.status === 'CANCELLED') throw apiError(409, 'This recipient cannot be sent another invitation', 'NOT_RESENDABLE');
    if (r.sendCount >= 1 + campaign.maxResends && ['SENT', 'EXPIRED', 'FAILED'].includes(r.status)) throw apiError(409, 'The resend limit of this campaign is reached', 'RESEND_LIMIT');
    if (!['SENT', 'EXPIRED', 'FAILED'].includes(r.status)) throw apiError(409, 'This recipient cannot be sent another invitation', 'NOT_RESENDABLE');
    this.assertNoBlockers(actor.tenantId);
    if (this.deliver(campaign, r) !== 'sent') throw apiError(409, 'This recipient cannot be sent another invitation', 'NOT_RESENDABLE');
    return { recipient: this.recipientView(campaign, r), delivery: 'QUEUED' };
  }
}
